import { c as _c } from "react/compiler-runtime";
import React from "react";
import { X, Calendar, GitCommitHorizontal, GitBranch, GitPullRequestArrow } from "lucide-react";
import { TestHistoryRun } from "@/hooks/useTestHistory";
import { formatDistanceToNow } from "date-fns";
import { LogLineText } from "./LogLineText";
import { Avatar, AvatarFallback, AvatarImage } from "@repo/blacksmith-ui/avatar";
import { formatTestDuration } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@repo/blacksmith-ui/tooltip";
interface TestFailureCardProps {
  run: TestHistoryRun;
  maxDurationMs: number; // Maximum duration (ms) for proportional bar calculation
  org: string;
  testId: string;
  repository: string;
  timeRangeHours?: number; // Time range to preserve in URL
  branchFilter?: "all" | "main" | "current" | "main_and_current" | "latest_per_branch"; // Branch filter to preserve in URL
  testType?: "junit" | "auto"; // Test type to show badge
  showErrorPreview?: boolean; // Whether to show error log preview in card
  actorMap?: Map<string, string>; // Map of login -> avatar_url for avatar lookups
  onCardClick?: (run: TestHistoryRun) => void;
  isSuperuser?: boolean; // Whether the current user is a superuser (shows similarity score)
  onMouseEnter?: (testId: string) => void; // Callback when hovering over the card
  onMouseLeave?: () => void; // Callback when leaving the card
}
export const TestFailureCard = React.memo(function TestFailureCard(t0) {
  const $ = _c(71);
  const {
    run,
    maxDurationMs,
    showErrorPreview: t1,
    actorMap,
    onCardClick,
    isSuperuser: t2,
    onMouseEnter,
    onMouseLeave
  } = t0;
  const showErrorPreview = t1 === undefined ? false : t1;
  const isSuperuser = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== run.duration_seconds) {
    t3 = formatTestDuration(run.duration_seconds);
    $[0] = run.duration_seconds;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const durationText = t3;
  const showDurationIndicator = maxDurationMs > 0;
  let t4;
  bb0: {
    if (run.duration_seconds == null || maxDurationMs <= 0) {
      t4 = 0;
      break bb0;
    }
    const durationMs = Math.max(0, run.duration_seconds * 1000);
    const ratio = durationMs / maxDurationMs;
    let t5;
    if ($[2] !== ratio) {
      t5 = Math.round(ratio * 100);
      $[2] = ratio;
      $[3] = t5;
    } else {
      t5 = $[3];
    }
    const pct = t5;
    t4 = Math.max(0, Math.min(100, pct));
  }
  const durationWidthPercent = t4;
  const parseUTCTimestamp = _temp;
  let t5;
  let timestamp;
  if ($[4] !== run.timestamp) {
    const exactTimestampDate = run.timestamp ? parseUTCTimestamp(run.timestamp) : null;
    timestamp = exactTimestampDate ? formatDistanceToNow(exactTimestampDate, {
      addSuffix: true
    }) : "Unknown";
    t5 = exactTimestampDate && Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      timeZoneName: "short"
    }).format(exactTimestampDate);
    $[4] = run.timestamp;
    $[5] = t5;
    $[6] = timestamp;
  } else {
    t5 = $[5];
    timestamp = $[6];
  }
  const exactTimestampLocal = t5;
  let t6;
  if ($[7] !== run.sha) {
    t6 = run.sha ? run.sha.substring(0, 7) : "\u2014";
    $[7] = run.sha;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const shortSha = t6;
  const errorLogs = run.logs || "No error details available";
  let t7;
  if ($[9] !== actorMap || $[10] !== durationText || $[11] !== durationWidthPercent || $[12] !== errorLogs || $[13] !== exactTimestampLocal || $[14] !== isSuperuser || $[15] !== onCardClick || $[16] !== onMouseEnter || $[17] !== onMouseLeave || $[18] !== run || $[19] !== shortSha || $[20] !== showDurationIndicator || $[21] !== showErrorPreview || $[22] !== timestamp) {
    const logLines = errorLogs.split("\n").filter(_temp2);
    const lineCount = logLines.length;
    const previewLines = logLines.slice(0, 2);
    const remainingLines = lineCount - 2;
    let t8;
    if ($[24] !== onCardClick || $[25] !== run) {
      t8 = () => {
        onCardClick?.(run);
      };
      $[24] = onCardClick;
      $[25] = run;
      $[26] = t8;
    } else {
      t8 = $[26];
    }
    const handleCardClick = t8;
    let t9;
    if ($[27] !== onMouseEnter || $[28] !== run.test_id) {
      t9 = () => {
        if (run.test_id) {
          onMouseEnter?.(run.test_id);
        }
      };
      $[27] = onMouseEnter;
      $[28] = run.test_id;
      $[29] = t9;
    } else {
      t9 = $[29];
    }
    let t10;
    if ($[30] !== onMouseLeave) {
      t10 = () => onMouseLeave?.();
      $[30] = onMouseLeave;
      $[31] = t10;
    } else {
      t10 = $[31];
    }
    let t11;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <div className="flex-shrink-0 size-4 flex items-center justify-center"><div className="size-4 rounded-full border border-[#FC6067] flex items-center justify-center"><X className="size-[10px] text-[#FC6067]" strokeWidth={2.5} /></div></div>;
      $[32] = t11;
    } else {
      t11 = $[32];
    }
    let t12;
    if ($[33] !== run.sha || $[34] !== shortSha) {
      t12 = run.sha && <div className="flex items-center gap-1 bg-neutral-100 dark:bg-neutral-800 h-[19px] px-1 py-0.5 rounded-sm flex-shrink-0"><GitCommitHorizontal className="size-4 text-neutral-500 dark:text-neutral-400" /><span className="type-body-xs-regular text-neutral-900 dark:text-neutral-100">{shortSha}</span></div>;
      $[33] = run.sha;
      $[34] = shortSha;
      $[35] = t12;
    } else {
      t12 = $[35];
    }
    let t13;
    if ($[36] !== run.commit_message) {
      t13 = run.commit_message && <span className="type-body-s-medium text-neutral-900 dark:text-neutral-100 truncate min-w-0">{run.commit_message.split("\n")[0]}</span>;
      $[36] = run.commit_message;
      $[37] = t13;
    } else {
      t13 = $[37];
    }
    let t14;
    if ($[38] !== isSuperuser || $[39] !== run.similarity_score) {
      t14 = isSuperuser && run.similarity_score != null && <TooltipProvider><Tooltip><TooltipTrigger asChild={true}><div className="flex items-center gap-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded text-xs font-medium cursor-help flex-shrink-0">{Math.round(run.similarity_score * 100)}% match</div></TooltipTrigger><TooltipContent side="top" className="max-w-xs"><p>Similarity score comparing this failure's error signature to the current failure. Higher scores indicate more similar error patterns.</p></TooltipContent></Tooltip></TooltipProvider>;
      $[38] = isSuperuser;
      $[39] = run.similarity_score;
      $[40] = t14;
    } else {
      t14 = $[40];
    }
    let t15;
    if ($[41] !== t12 || $[42] !== t13 || $[43] !== t14) {
      t15 = <div className="flex items-center min-w-0"><div className="flex items-center gap-2 min-w-0 flex-1">{t11}{t12}{t13}{t14}</div></div>;
      $[41] = t12;
      $[42] = t13;
      $[43] = t14;
      $[44] = t15;
    } else {
      t15 = $[44];
    }
    let t16;
    if ($[45] !== actorMap || $[46] !== run.user) {
      t16 = run.user && <div className="flex items-center gap-1 min-w-0"><Avatar className="w-3.5 h-3.5"><AvatarImage src={actorMap?.get(run.user)} /><AvatarFallback className="text-[7px] bg-gradient-to-br from-green-400 to-blue-500">{run.user.slice(0, 2).toUpperCase()}</AvatarFallback></Avatar><span className="type-body-xs-regular text-neutral-600 dark:text-neutral-400 truncate">{run.user}</span></div>;
      $[45] = actorMap;
      $[46] = run.user;
      $[47] = t16;
    } else {
      t16 = $[47];
    }
    let t17;
    if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <Calendar className="w-3.5 h-3.5 text-neutral-500 dark:text-neutral-400" />;
      $[48] = t17;
    } else {
      t17 = $[48];
    }
    let t18;
    if ($[49] !== exactTimestampLocal || $[50] !== timestamp) {
      t18 = <div className="flex items-center gap-1 min-w-0">{t17}{exactTimestampLocal ? <TooltipProvider><Tooltip><TooltipTrigger asChild={true}><span className="type-body-xs-regular text-neutral-600 dark:text-neutral-400 whitespace-nowrap">{timestamp}</span></TooltipTrigger><TooltipContent side="top"><p>{exactTimestampLocal}</p></TooltipContent></Tooltip></TooltipProvider> : <span className="type-body-xs-regular text-neutral-600 dark:text-neutral-400 whitespace-nowrap">{timestamp}</span>}</div>;
      $[49] = exactTimestampLocal;
      $[50] = timestamp;
      $[51] = t18;
    } else {
      t18 = $[51];
    }
    let t19;
    if ($[52] !== run.pr_number) {
      t19 = run.pr_number && <div className="flex items-center gap-1 min-w-0"><GitPullRequestArrow className="w-3.5 h-3.5 text-neutral-500 dark:text-neutral-400" /><span className="type-body-xs-regular text-neutral-600 dark:text-neutral-400 truncate">PR #{run.pr_number}</span></div>;
      $[52] = run.pr_number;
      $[53] = t19;
    } else {
      t19 = $[53];
    }
    let t20;
    if ($[54] !== run.branch) {
      t20 = run.branch && <div className="flex items-center gap-1 min-w-0 flex-1"><GitBranch className="w-3.5 h-3.5 text-neutral-500 dark:text-neutral-400 flex-shrink-0" /><span className="type-body-xs-regular text-neutral-600 dark:text-neutral-400 truncate min-w-0" title={run.branch}>{run.branch}</span></div>;
      $[54] = run.branch;
      $[55] = t20;
    } else {
      t20 = $[55];
    }
    let t21;
    if ($[56] !== t16 || $[57] !== t18 || $[58] !== t19 || $[59] !== t20) {
      t21 = <div className="flex items-center gap-4 pl-6 min-w-0">{t16}{t18}{t19}{t20}</div>;
      $[56] = t16;
      $[57] = t18;
      $[58] = t19;
      $[59] = t20;
      $[60] = t21;
    } else {
      t21 = $[60];
    }
    let t22;
    if ($[61] !== t15 || $[62] !== t21) {
      t22 = <div className="min-w-0 flex-1 flex flex-col gap-0.5">{t15}{t21}</div>;
      $[61] = t15;
      $[62] = t21;
      $[63] = t22;
    } else {
      t22 = $[63];
    }
    let t23;
    if ($[64] !== durationText || $[65] !== durationWidthPercent || $[66] !== showDurationIndicator) {
      t23 = showDurationIndicator && <div className="flex flex-col items-start gap-1 flex-shrink-0"><span className="type-body-xs-regular text-neutral-900 dark:text-neutral-100 whitespace-nowrap">{durationText}</span><div className="bg-neutral-200 dark:bg-neutral-800 rounded-full w-[126px] h-1 overflow-hidden flex items-center"><div className="bg-neutral-800 dark:bg-neutral-200 h-1 rounded-full" style={{
            width: `${durationWidthPercent}%`
          }} /></div></div>;
      $[64] = durationText;
      $[65] = durationWidthPercent;
      $[66] = showDurationIndicator;
      $[67] = t23;
    } else {
      t23 = $[67];
    }
    let t24;
    if ($[68] !== t22 || $[69] !== t23) {
      t24 = <div className="flex items-start gap-3 justify-between px-4 py-3">{t22}{t23}</div>;
      $[68] = t22;
      $[69] = t23;
      $[70] = t24;
    } else {
      t24 = $[70];
    }
    t7 = <div onClick={handleCardClick} onMouseEnter={t9} onMouseLeave={t10} className="cursor-pointer hover:bg-[var(--color-surface-hover)]">{t24}{showErrorPreview && run.logs && lineCount > 0 && <div className="px-4 pb-3"><div className="bg-white dark:bg-[#2A2C2C] border border-[var(--color-border)] dark:border-[var(--color-border)] rounded px-2 py-1"><div className="flex-1 min-w-0"><div className="text-xs leading-4 whitespace-pre-wrap">{previewLines.map(_temp3)}{remainingLines > 0 && <div className="text-[#737373] dark:text-[#A3A3A3] mt-1">... {remainingLines} more line{remainingLines !== 1 ? "s" : ""}</div>}</div></div></div></div>}</div>;
    $[9] = actorMap;
    $[10] = durationText;
    $[11] = durationWidthPercent;
    $[12] = errorLogs;
    $[13] = exactTimestampLocal;
    $[14] = isSuperuser;
    $[15] = onCardClick;
    $[16] = onMouseEnter;
    $[17] = onMouseLeave;
    $[18] = run;
    $[19] = shortSha;
    $[20] = showDurationIndicator;
    $[21] = showErrorPreview;
    $[22] = timestamp;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
});
function _temp(ts) {
  if (ts.endsWith("Z") || /[+-]\d{2}:\d{2}$/.test(ts)) {
    return new Date(ts);
  }
  const isoFormat = ts.replace(" ", "T") + "Z";
  return new Date(isoFormat);
}
function _temp2(line) {
  return line.trim() !== "";
}
function _temp3(line_0, index) {
  return <div key={index}><LogLineText text={line_0} searchTerms={[]} /></div>;
}